import React, { useState, useEffect, useRef } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import styled from '@emotion/styled';
import EnhancedColorPicker from './EnhancedColorPicker';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

const EditorWrapper = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  margin-bottom: 16px;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #40a9ff;
  }
  
  &:focus-within {
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
  
  .rdw-editor-wrapper {
    min-height: 150px;
  }
  
  .rdw-editor-main {
    padding: 12px 16px;
    min-height: 120px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    font-size: 14px;
    line-height: 1.5;
  }
  
  .rdw-editor-toolbar {
    border: none;
    border-bottom: 1px solid #f0f0f0;
    margin-bottom: 0;
    padding: 8px 12px;
    background: #fafafa;
    border-radius: 6px 6px 0 0;
  }
  
  .rdw-option-wrapper {
    border: 1px solid transparent;
    border-radius: 4px;
    margin: 0 2px;
    
    &:hover {
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    }
  }
  
  .rdw-option-active {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    background: #e6f7ff;
    border-color: #91d5ff;
  }
  
  .public-DraftStyleDefault-block {
    margin: 8px 0;
  }
  
  .public-DraftEditorPlaceholder-root {
    color: #bfbfbf;
    pointer-events: none;
  }
  
  .public-DraftEditorPlaceholder-hasFocus {
    color: #bfbfbf;
  }
`;

const RichTextEditor = ({ value, onChange, placeholder = 'Text eingeben...' }) => {
  const [editorState, setEditorState] = useState(() => {
    if (value) {
      const contentBlock = htmlToDraft(value);
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        return EditorState.createWithContent(contentState);
      }
    }
    return EditorState.createEmpty();
  });

  const isUserChange = useRef(false);
  const lastValue = useRef(value);

  useEffect(() => {
    // Only update if this is an external change and the value is actually different
    if (!isUserChange.current && value !== lastValue.current) {
      lastValue.current = value;

      if (value) {
        const contentBlock = htmlToDraft(value);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
          setEditorState(EditorState.createWithContent(contentState));
        }
      } else {
        setEditorState(EditorState.createEmpty());
      }
    }

    // Reset the user change flag
    isUserChange.current = false;
  }, [value]);

  const handleEditorChange = (newEditorState) => {
    isUserChange.current = true;
    setEditorState(newEditorState);

    const html = draftToHtml(convertToRaw(newEditorState.getCurrentContent()));
    lastValue.current = html;
    onChange(html);
  };

  const toolbarConfig = {
    options: ['inline', 'list', 'colorPicker', 'history'],
    inline: {
      options: ['bold', 'italic', 'underline'],
      bold: { className: 'rdw-option' },
      italic: { className: 'rdw-option' },
      underline: { className: 'rdw-option' },
    },
    list: {
      options: ['unordered', 'ordered'],
      unordered: { className: 'rdw-option' },
      ordered: { className: 'rdw-option' },
    },
    colorPicker: {
      component: EnhancedColorPicker,
    },
    history: {
      undo: { className: 'rdw-option' },
      redo: { className: 'rdw-option' },
    },
  };

  return (
    <div>
      <EditorWrapper>
        <Editor
          editorState={editorState}
          onEditorStateChange={handleEditorChange}
          toolbar={toolbarConfig}
          placeholder={placeholder}
          stripPastedStyles={true}
          editorClassName="rdw-editor"
          toolbarClassName="rdw-toolbar"
        />
      </EditorWrapper>
    </div>
  );
};

export default RichTextEditor;
